'use client';

import { CheckBox, Visibility, VisibilityOff } from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { PRIMARY } from '../../../../libs/mui';
import { useNavigate } from 'react-router-dom';

interface IFormInput {
  passwordConfirm: string;
  password: string;
  email: string;
  firstName: string;
  lastName: string;
}

const generateUUID = () => {
  // generate a synthetic UUID
  // we only need it to be 10 random letters or numbers

  return Math.random().toString(36).substring(2, 12).toLowerCase();
};

interface Props {
  onSubmit: (
    email: string,
    password: string,
    isAnnual: boolean,
    firstName?: string,
    lastName?: string
  ) => void;
  isSubmitting?: boolean;
}

const SignUpPasswords = ({ onSubmit, isSubmitting }: Props) => {
  const [isAnnual, setIsAnnual] = useState(true);
  const { control, handleSubmit, getValues } = useForm<IFormInput>({
    defaultValues: {
      email: ``,
      passwordConfirm: '',
      password: '',
      firstName: '',
      lastName: '',
    },
    mode: 'onChange',
    delayError: 600,
  });

  const router = useNavigate();

  const [visibilityOn, setVisibilityOn] = useState(false);

  const handleSubmitWithAnimation = (data: IFormInput) => {
    onSubmit(
      data.email,
      data.password,
      isAnnual,
      data.firstName,
      data.lastName
    );
  };

  const [checked, setChecked] = useState(false);
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(event.target.checked);
  };

  return (
    <form autoComplete="off" onSubmit={handleSubmit(handleSubmitWithAnimation)}>
      <AnimatePresence>
        {!isSubmitting && (
          <motion.div
            initial={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 0.4 } }}
          >
            <Grid container spacing={2} mb={2}>
              <Grid item xs={12} lg={6}>
                <Controller
                  name="firstName"
                  control={control}
                  rules={{
                    required: 'First name is required',
                    pattern: {
                      value: /^(?=.*[a-zA-Z]).{2,}$/,
                      message: 'Enter a valid first name.',
                    },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      type="text"
                      size="small"
                      placeholder="First Name"
                      fullWidth
                      autoComplete="off"
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} lg={6}>
                <Controller
                  name="lastName"
                  control={control}
                  rules={{
                    required: 'Last name is required',
                    pattern: {
                      value: /^(?=.*[a-zA-Z]).{2,}$/,
                      message: 'Enter a valid first name.',
                    },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      type="text"
                      size="small"
                      placeholder="Last Name"
                      fullWidth
                      autoComplete="off"
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Grid>
            </Grid>
            <Controller
              name="email"
              control={control}
              rules={{
                required: 'Email is required',
                pattern: {
                  value: /^[^@ ]+@[^@ ]+\.[^@ .]{2,}$/,
                  message: 'Please enter a valid email address.',
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  type="text"
                  size="small"
                  placeholder="Email"
                  fullWidth
                  autoComplete="new-password"
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
            <Grid container spacing={2} mb={2} sx={{ mt: 0 }}>
              <Grid item xs={12} lg={6}>
                <Controller
                  name="password"
                  control={control}
                  rules={{
                    required: 'Password is required',
                    pattern: {
                      value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{7,}$/,
                      message:
                        '7 characters long + one of each: uppercase, lowercase, number, special character',
                    },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      type={visibilityOn ? 'text' : 'password'}
                      placeholder="Password"
                      fullWidth
                      autoComplete="new-password"
                      size="small"
                      error={!!error}
                      helperText={error?.message}
                      InputProps={{
                        endAdornment: (
                          <IconButton
                            onClick={() => setVisibilityOn(!visibilityOn)}
                          >
                            {visibilityOn ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} lg={6}>
                <Controller
                  name="passwordConfirm"
                  control={control}
                  rules={{
                    required: 'Password confirmation is required',
                    validate: (value) =>
                      value === getValues('password') ||
                      'Passwords do not match',
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      type={visibilityOn ? 'text' : 'password'}
                      placeholder="Confirm Password"
                      size="small"
                      fullWidth
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Grid>
            </Grid>

            <Typography
              style={{ fontWeight: 700 }}
              my={1}
              mt={4}
              color="text.secondary"
              variant="h5"
            >
              Step 2
            </Typography>

            <Typography mb={0.5} variant="h3">
              Choose Your Plan
            </Typography>

            <Typography mb={3} variant="body2">
              Cancel anytime during <b>10-day FREE trial</b> period
            </Typography>

            <Box display="flex" gap={2}>
              <Box
                onClick={() => setIsAnnual(true)}
                sx={{
                  backgroundColor: isAnnual ? '#F4F8FF' : '#fff',
                  borderRadius: 1,
                  padding: 2,
                  border: isAnnual
                    ? `1px solid ${PRIMARY}`
                    : `1px solid #C4C4C4`,
                  cursor: 'pointer',
                  flex: 1,
                }}
              >
                <Box
                  display={'flex'}
                  alignItems="center"
                  justifyContent={'space-between'}
                >
                  <Typography
                    variant="h5"
                    style={{ fontWeight: 600, fontSize: 20, marginBottom: 2 }}
                  >
                    Annual
                  </Typography>

                  <Checkbox
                    sx={{
                      color: PRIMARY,
                      '&.Mui-checked': {
                        color: PRIMARY,
                      },
                    }}
                    checked={isAnnual}
                  />
                </Box>
                <Typography
                  variant="h5"
                  style={{
                    fontSize: 24,
                    fontWeight: 400,
                    marginBottom: 8,
                  }}
                >
                  <b>$15</b>{' '}
                  <Typography component={'span'} variant="body1">
                    {' '}
                    /month
                  </Typography>
                </Typography>
                <Typography variant="body2" style={{}}>
                  Pay <b>$180</b> upfront and save 20%
                </Typography>
              </Box>

              <Box
                onClick={() => setIsAnnual(false)}
                sx={{
                  backgroundColor: !isAnnual ? '#F4F8FF' : '#fff',
                  borderRadius: 1,
                  padding: 2,
                  border: !isAnnual
                    ? `1px solid ${PRIMARY}`
                    : `1px solid #C4C4C4`,
                  cursor: 'pointer',
                  flex: 1,
                }}
              >
                <Box
                  display={'flex'}
                  alignItems="center"
                  justifyContent={'space-between'}
                >
                  <Typography
                    variant="h5"
                    style={{ fontWeight: 600, fontSize: 20, marginBottom: 2 }}
                  >
                    Monthly
                  </Typography>

                  <Checkbox
                    sx={{
                      color: PRIMARY,
                      '&.Mui-checked': {
                        color: PRIMARY,
                      },
                    }}
                    checked={!isAnnual}
                  />
                </Box>
                <Typography
                  variant="h5"
                  style={{
                    fontSize: 24,
                    fontWeight: 400,
                    marginBottom: 8,
                  }}
                >
                  <b>$19</b>{' '}
                  <Typography component={'span'} variant="body1">
                    {' '}
                    /month
                  </Typography>
                </Typography>
                <Typography variant="body2" style={{}}>
                  Pay monthly. Cancel anytime.
                </Typography>
              </Box>
            </Box>

            <Box display={'flex'} alignItems="center" mt={2} mb={1}>
              <Checkbox
                sx={{
                  color: PRIMARY,
                  '&.Mui-checked': {
                    color: PRIMARY,
                  },
                }}
                checked={checked}
                onChange={(e) => setChecked(e.target.checked)}
              />

              <Typography
                variant="body2"
                style={{
                  fontSize: 14,
                  fontWeight: 400,
                  marginLeft: 1,
                }}
              >
                I agree to Options AI{' '}
                <Typography
                  component={'span'}
                  variant="body2"
                  style={{
                    fontSize: 14,
                    fontWeight: 400,
                    textDecoration: 'underline',
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    // open in new tab
                    window.open(`https://www.optionsai.com/terms/`, '_blank');
                  }}
                >
                  Terms
                </Typography>
              </Typography>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>

      <Box mt={2}>
        {!isSubmitting ? (
          <>
            <Button
              fullWidth
              variant="contained"
              size="large"
              type="submit"
              disabled={isSubmitting || !checked}
              disableElevation
            >
              Next
            </Button>

            <Button
              variant="text"
              size="small"
              sx={{ textTransform: 'none', fontSize: 14, marginTop: 2 }}
              onClick={() => {
                router('/sign-in');
              }}
            >
              Already have an account? Sign In
            </Button>
          </>
        ) : (
          <motion.div
            animate={{
              top: 200,
            }}
            transition={{
              duration: 1.5,
              ease: 'linear',
            }}
            style={{
              position: 'absolute',
              top: 400,
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <motion.div
                  animate={{ rotate: 123 }}
                  transition={{ duration: 1.3, ease: 'linear', delay: 1.5 }}
                  style={{
                    width: 100,
                    height: 100,
                    border: '19px solid #000',
                    borderBottomColor: '#fff',
                    borderRadius: '50%',
                    display: 'inline-block',
                    boxSizing: 'border-box',
                  }}
                />

                <div style={{ display: 'flex', marginLeft: 0, marginTop: 20 }}>
                  {'ptions'.split('').map((char, index) => (
                    <motion.span
                      key={index}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{
                        delay: 3 + index * 0.1,
                        duration: 0.4,
                        ease: 'easeOut',
                      }}
                      style={{
                        fontSize: 100 * 0.75,
                        fontWeight: 900,
                        letterSpacing: -2,
                        color: '#000',
                      }}
                    >
                      {char}
                    </motion.span>
                  ))}

                  <motion.div
                    initial={{ opacity: 0, scale: 0.5, y: 50 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{
                      delay: 3 + 'ptions'.length * 0.1 + 0.2,
                      type: 'spring',
                      stiffness: 400,
                      damping: 15,
                    }}
                    style={{
                      backgroundColor: '#007aff',
                      color: 'white',
                      fontWeight: '900',
                      marginTop: 20,
                      fontSize: 40,
                      height: 70,
                      padding: '0 12px 0',
                      borderRadius: 16,
                      marginLeft: 16,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      lineHeight: 1,
                      paddingTop: 0,
                    }}
                  >
                    AI
                  </motion.div>
                </div>
              </div>

              {/* Creating your account... */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 5.5, duration: 0.4 }}
                style={{
                  marginTop: 10,
                  display: 'flex',
                  alignItems: 'center',
                  marginLeft: 20,
                }}
              >
                <Typography
                  style={{ fontSize: 16, fontWeight: 400, marginRight: 1 }}
                >
                  Creating your account
                </Typography>
                <div style={{ display: 'flex' }}>
                  {[0, 1, 2].map((dotIndex) => (
                    <motion.span
                      key={dotIndex}
                      initial={{ opacity: 1, y: 0 }}
                      animate={{ opacity: 1, y: [0, -8, 0] }}
                      transition={{
                        delay: 5.5 + dotIndex * 0.2,
                        duration: 0.6,
                        repeat: Infinity,
                        repeatDelay: 0.5,
                      }}
                      style={{
                        fontSize: 17,
                        fontWeight: 700,
                        margin: '0 1px',
                      }}
                    >
                      .
                    </motion.span>
                  ))}
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </Box>
    </form>
  );
};

export default SignUpPasswords;
